// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.

import type { PDFDocumentProxy } from 'pdfjs-dist';
import type {
  AssembleRequest,
  AssembleResponse,
  Box,
  ExportSettings,
  FlatPage,
  PageInfo,
} from './types';
import { boxToPixels, padBox, planFlatten, scaleForDpi } from './redact';
import { renderPageToCanvas } from './pdf';

export interface ExportResult {
  blob: Blob;
  flattenedPages: number;
}

export type ProgressFn = (phase: string, pct: number) => void;

/** Yield to the event loop without rAF (which a hidden tab throttles to zero). */
const yieldToLoop = () => new Promise<void>((r) => setTimeout(r, 0));

function canvasToBlob(canvas: HTMLCanvasElement, mime: string, quality?: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('Could not encode a redacted page.'))),
      mime,
      quality,
    );
  });
}

/**
 * Build the redacted PDF. Redacted (and, if requested, all) pages are
 * rasterised on the main thread — pdf.js must render to a canvas — with the
 * black bars painted into the pixels; the pdf-lib re-assembly runs in a worker.
 */
export async function exportRedacted(
  doc: PDFDocumentProxy,
  sourceBytes: ArrayBuffer,
  pages: PageInfo[],
  boxesByPage: Map<number, Box[]>,
  settings: ExportSettings,
  onProgress: ProgressFn,
): Promise<ExportResult> {
  const plan = planFlatten(boxesByPage, pages.length, settings.flattenAll);
  const scale = scaleForDpi(settings.dpi);
  const mime: 'image/png' | 'image/jpeg' = settings.sharp ? 'image/png' : 'image/jpeg';
  const quality = settings.sharp ? undefined : 0.92;

  const canvas = document.createElement('canvas');
  const flat: FlatPage[] = [];

  for (let k = 0; k < plan.length; k++) {
    const index = plan[k];
    const info = pages[index];
    await renderPageToCanvas(doc, index, scale, canvas);

    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) throw new Error('Canvas 2D is unavailable in this browser.');
    ctx.fillStyle = '#000000';
    for (const box of boxesByPage.get(index) ?? []) {
      const r = boxToPixels(padBox(box, 0.006), canvas.width, canvas.height);
      if (r.w > 0 && r.h > 0) ctx.fillRect(r.x, r.y, r.w, r.h);
    }

    const blob = await canvasToBlob(canvas, mime, quality);
    const bytes = await blob.arrayBuffer();
    flat.push({ index, bytes, mime, width: info.width, height: info.height });

    onProgress('Rasterising pages…', Math.round(((k + 1) / Math.max(1, plan.length)) * 78));
    await yieldToLoop();
  }

  onProgress('Rebuilding PDF…', 80);
  const outBytes = await runAssembly(
    { source: sourceBytes.slice(0), flat, flattenAll: settings.flattenAll },
    onProgress,
  );

  return { blob: new Blob([outBytes], { type: 'application/pdf' }), flattenedPages: plan.length };
}

/** Spin up the assembly worker, hand it the source + flat pages (transferred),
 *  and await the finished PDF bytes. The worker is always terminated. */
function runAssembly(req: AssembleRequest, onProgress: ProgressFn): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const worker = new Worker(new URL('./workers/assemble.ts', import.meta.url), {
      type: 'module',
    });
    const done = (fn: () => void) => {
      worker.terminate();
      fn();
    };
    worker.onmessage = (
      e: MessageEvent<AssembleResponse | { type: 'progress'; pct: number }>,
    ) => {
      const msg = e.data;
      if ('type' in msg && msg.type === 'progress') {
        onProgress('Rebuilding PDF…', 80 + Math.round((msg.pct / 100) * 20));
        return;
      }
      if ('ok' in msg && msg.ok) done(() => resolve(msg.bytes));
      else done(() => reject(new Error(('ok' in msg && msg.error) || 'Assembly failed.')));
    };
    worker.onerror = (err) => done(() => reject(new Error(err.message || 'Assembly worker error.')));

    const transfer: Transferable[] = [req.source, ...req.flat.map((f) => f.bytes)];
    worker.postMessage(req, transfer);
  });
}
